import { skipToken } from '@reduxjs/toolkit/query'
import { useMemo, useState } from 'react'

import { queryView, useOpenWindow, useWindow } from '@krp/client-runtime'
import { PLATFORM_WINDOWS, platformMyResources } from '@krp/platform-ui'
import {
  AQSI_WINDOWS,
  FEEDBACK_SCOPES,
  accessKeyOf,
  aqsiCashierRmkSettings,
  aqsiOperation,
  aqsiOwnCashiers,
  aqsiPosCashiers,
  aqsiPriceTypes,
  aqsiRegisters,
  aqsiSessionJournal,
  aqsiShift,
  createRmkActions,
  hasRmkRight,
  makeSelectRmkVm,
  mineDeviceId,
  mineRegisterKey,
  openingOf,
  pendingForWindow,
  ridKey,
  rmkReceiptsOpened,
  startCashierIdOf,
  startMemoryOf,
  shiftReadKey,
  useAccessExpiry,
  useRmkHeartbeat,
  useShiftProgress,
} from '@krp/resource-aqsi-ui'
import { isoDayOf } from '@krp/ui-kit'

import { useAppDispatch, useAppSelector } from '../../app/hooks'
import type { RootState } from '../../app/types'
import { useRefetchOnReconnect } from '../../app/connection'
import { AQSI_MINE } from './aqsiMine'
import { useShiftReadDelay } from './useShiftReadDelay'
import { useMySession } from './useMySession'
import { useRegisterLinks } from './useRegisterLinks'

/** Список зависит от прав: окно, открытое заново, читает его заново, а не из кэша. */
const FRESH_RIGHTS = { refetchOnMountOrArgChange: true } as const

const selectTakeoverSources = platformMyResources.select(AQSI_MINE.takeover)
const selectCloseAnySources = platformMyResources.select(AQSI_MINE.closeAny)
const selectJournalSources = platformMyResources.select(AQSI_MINE.journal)
const selectRefundSources = platformMyResources.select(AQSI_MINE.refunds)

/**
 * Данные кассового потока одного источника для окна «РМК»: кассы и кассиры кабинета, моя работа (наша
 * база), смена, команда смены, связь двух уровней, служебные права, выбор «Начала работы» и подтверждение кода кассира. rid — из контекста окна (адрес), не из
 * «текущего ресурса»: окно AQSI-A не начинает показывать AQSI-B. Продажу и отложенные чеки ведёт само окно РМК.
 */
export function useRmkModel() {
  const window = useWindow()
  const dispatch = useAppDispatch()
  const openWindow = useOpenWindow()
  const resourceId = window.params.rid ?? ''
  const args = useMemo(() => ({ resourceId }), [resourceId])
  const skip = resourceId === ''

  platformMyResources.useQuerySubscription(AQSI_MINE.rmk, FRESH_RIGHTS)
  platformMyResources.useQuerySubscription(AQSI_MINE.sales, FRESH_RIGHTS)
  platformMyResources.useQuerySubscription(AQSI_MINE.price, FRESH_RIGHTS)
  // Служебные права окна (освободить кассу, журнал) — «мои ресурсы» с этим правом: кассир служебного не видит,
  // а сервер всё равно проверяет то же право.
  platformMyResources.useQuerySubscription(AQSI_MINE.takeover, FRESH_RIGHTS)
  platformMyResources.useQuerySubscription(AQSI_MINE.closeAny, FRESH_RIGHTS)
  platformMyResources.useQuerySubscription(AQSI_MINE.journal, FRESH_RIGHTS)
  platformMyResources.useQuerySubscription(AQSI_MINE.refunds, FRESH_RIGHTS)
  const takeover = hasRmkRight(useAppSelector(selectTakeoverSources).data, resourceId)
  const closeAny = hasRmkRight(useAppSelector(selectCloseAnySources).data, resourceId)
  const journalRight = hasRmkRight(useAppSelector(selectJournalSources).data, resourceId)
  // Возврат из РМК (RP-FB74): плитка только с правом `refunds.manage`; сервер всё равно проверяет то же право.
  const canRefund = hasRmkRight(useAppSelector(selectRefundSources).data, resourceId)
  const rights = useMemo(() => ({ takeover, closeAny, journal: journalRight }), [closeAny, journalRight, takeover])

  // Кассы читает кабинет; моя работа — наша база: этап окна и идущая продажа от кабинета не зависят.
  aqsiRegisters.useQuerySubscription(args, { skip })
  const selectRegisters = useMemo(() => aqsiRegisters.select(args), [args])
  const registers = useAppSelector(selectRegisters).data
  const { select: selectMySession, session: mine } = useMySession(resourceId)

  const mineKey = mineRegisterKey(mine)
  const mineDevice = mineDeviceId(mine)
  const deviceIds = useMemo(
    () => [...(registers?.registers.map((register) => register.deviceId) ?? []), ...(mineDevice !== null ? [mineDevice] : [])],
    [mineDevice, registers],
  )
  const links = useRegisterLinks(resourceId, deviceIds)

  const pending = useAppSelector((state: RootState) => pendingForWindow(state.aqsi.operations, resourceId, mineKey))
  const shiftArgs = useMemo(
    () => (mineDevice === null ? null : { resourceId, deviceId: mineDevice }),
    [mineDevice, resourceId],
  )
  const shiftOperationArgs = useMemo(
    () =>
      pending === undefined || pending.phase === 'sending' || pending.phase === 'not-found'
        ? null
        : { resourceId, operationId: pending.operationId },
    [pending, resourceId],
  )
  const shiftKey = mineDevice === null ? '' : shiftReadKey(resourceId, mineDevice)
  const lastShiftRead = useAppSelector((state: RootState) => state.aqsi.shiftReads[shiftKey])
  const shiftReadAt = useShiftReadDelay(shiftKey, lastShiftRead)
  // Кассир может закрыть смену на терминале. Пока окно РМК открыто, перечитываем
  // фискальный факт и сразу убираем продажу после подтверждённого закрытия.
  const selectShiftResult = useMemo(() => shiftArgs === null ? null : aqsiShift.select(shiftArgs), [shiftArgs])
  const shiftResult = useAppSelector((state: RootState) => selectShiftResult?.(state))
  const shiftSubscription = aqsiShift.useQuerySubscription(shiftReadAt !== null ? skipToken : shiftArgs ?? skipToken, {
    pollingInterval: shiftResult?.isError === true ? 0 : 15_000,
    skipPollingIfUnfocused: true,
  })
  // Связь вернулась — смена перечитывается сразу, не дожидаясь следующего опроса.
  useRefetchOnReconnect(shiftSubscription.refetch, shiftReadAt === null && shiftArgs !== null)
  // Статус нашей команды читается при открытии окна; дальше — кнопками «Проверить статус» и «Сверить».
  aqsiOperation.useQuerySubscription(shiftOperationArgs ?? skipToken)

  // «Журнал кассы» (право на журнал): читается, только пока диалог открыт.
  const [journalDevice, setJournalDevice] = useState<number | null>(null)
  const journalArgs = useMemo(
    () => (journalDevice === null || !journalRight ? null : { resourceId, deviceId: journalDevice }),
    [journalDevice, journalRight, resourceId],
  )
  aqsiSessionJournal.useQuerySubscription(journalArgs ?? skipToken)

  // Виды цен (RP-FB11): цена выбирается ДО открытия смены (RP-FB22), поэтому список читается и в «Начале работы».
  aqsiPriceTypes.useQuerySubscription(skip ? skipToken : args)
  // Кассиры источника (RP-FB22): нужны, пока своей работы за кассой нет; живое чтение кабинета — каждый заход читает заново.
  aqsiPosCashiers.useQuerySubscription(skip || mine !== null ? skipToken : args, FRESH_RIGHTS)
  const selectCashiers = useMemo(() => aqsiPosCashiers.select(args), [args])
  const access = useAppSelector((state: RootState) => state.aqsi.cashierAccess)
  // Подтверждение кода и блокировка снимаются в свой срок: блок «Код» открывается снова сам.
  useAccessExpiry(access, dispatch)

  // Персональные настройки РМК выбранного кассира (RP-CT6): читаются по его подтверждению `pos`, пока оно действует.
  const startMemory = useAppSelector((state: RootState) => startMemoryOf(state.aqsi.start, resourceId))
  const cashiersNow = useAppSelector((state: RootState) => selectCashiers(state).data)
  const startCashierId = startCashierIdOf(cashiersNow, startMemory)
  const startGrant = startCashierId === null ? undefined : access.proofs[accessKeyOf(resourceId, startCashierId)]?.proof
  const rmkArgs = useMemo(
    () => (skip || mine !== null || startCashierId === null || startGrant === undefined ? skipToken : { resourceId, cashierId: startCashierId, grant: startGrant }),
    [mine, resourceId, skip, startCashierId, startGrant],
  )
  aqsiCashierRmkSettings.useQuerySubscription(rmkArgs)
  const selectCashierRmk = useMemo(() => aqsiCashierRmkSettings.select(rmkArgs), [rmkArgs])

  const selectRmkVm = useMemo(() => {
    const selectPackageVm = makeSelectRmkVm()
    const selectSources = platformMyResources.select(AQSI_MINE.rmk)
    const selectShift = shiftArgs === null ? null : aqsiShift.select(shiftArgs)
    const selectOperation = shiftOperationArgs === null ? null : aqsiOperation.select(shiftOperationArgs)
    const selectJournal = journalArgs === null ? null : aqsiSessionJournal.select(journalArgs)
    const selectPriceTypes = aqsiPriceTypes.select(args)
    const feedbackKey = ridKey(resourceId, FEEDBACK_SCOPES.rmk)
    return (state: RootState) =>
      selectPackageVm({
        resourceId,
        sources: selectSources(state),
        registers: selectRegisters(state),
        mySession: selectMySession(state),
        shift: selectShift?.(state),
        operation: selectOperation?.(state),
        pending: pendingForWindow(state.aqsi.operations, resourceId, mineKey),
        feedback: state.aqsi.feedback.byKey[feedbackKey],
        rights,
        links,
        priceTypes: selectPriceTypes(state),
        cashiers: selectCashiers(state),
        cashierRmk: selectCashierRmk(state),
        startMemory: startMemoryOf(state.aqsi.start, resourceId),
        access: state.aqsi.cashierAccess,
        opening: openingOf(state.aqsi.opening, resourceId),
        ...(journalArgs !== null && selectJournal !== null
          ? { journal: { deviceId: journalArgs.deviceId, result: selectJournal(state) } }
          : {}),
      })
  }, [args, journalArgs, links, mineKey, resourceId, rights, selectCashierRmk, selectCashiers, selectMySession, selectRegisters, shiftArgs, shiftOperationArgs])
  const baseRmkVm = useAppSelector(selectRmkVm)
  useRmkHeartbeat(resourceId, baseRmkVm.mine, dispatch)
  const nextCheckAt = useShiftProgress(baseRmkVm, dispatch)
  // RP-CU4: кассиры нашей системы, связанные с кассирами этого кабинета, — вход в «Начале работы», пока своей работы за кассой нет, и перенос
  // работы кассира из другой вкладки его личным кодом (К-120).
  const ownNeeded = mine === null || (mine !== undefined && mine.thisTab === false && (mine.cashierId ?? null) !== null)
  const own = aqsiOwnCashiers.useQuery(skip || !ownNeeded ? skipToken : { resourceId })
  const { isUninitialized: ownIdle, isLoading: ownLoading, isError: ownFailed, isSuccess: ownReady, error: ownError, data: ownData } = own
  const ownCashiers = useMemo(() => ({
    query: queryView({ isUninitialized: ownIdle, isLoading: ownLoading, isError: ownFailed, isSuccess: ownReady,
      ...(ownError === undefined ? {} : { error: ownError }), ...(ownData === undefined ? {} : { data: ownData }) }, (items) => items.length === 0),
    items: ownData ?? [],
  }), [ownData, ownError, ownFailed, ownIdle, ownLoading, ownReady])
  const rmkVm = useMemo(() => ({ ...baseRmkVm, ownCashiers,
    shift: baseRmkVm.shift === null ? null : { ...baseRmkVm.shift, nextCheckAt: shiftReadAt },
    operation: baseRmkVm.operation === null ? null : { ...baseRmkVm.operation, nextCheckAt },
  }), [baseRmkVm, nextCheckAt, ownCashiers, shiftReadAt])

  const rmkActions = useMemo(
    () =>
      createRmkActions(rmkVm, dispatch, {
        // Рабочие окна (плитки источников, инциденты, чеки) открываются переходом на адрес —
        // это решает ядро (`useOpenWindow`); «← назад» вернёт сюда. Плитки из окна РМК автоперехода не делают.
        switchSource: () => openWindow(PLATFORM_WINDOWS.rmk),
        openJournal: setJournalDevice,
        closeJournal: () => setJournalDevice(null),
        openIncidents: () => openWindow(AQSI_WINDOWS.incidents, { rid: resourceId }),
        openPrintSettings: () => {
          if (mine) openWindow(AQSI_WINDOWS.printSettings, { rid: resourceId, deviceId: String(mine.deviceId) })
        },
        openReceipts: () => {
          if (mine === null || mine === undefined) return
          dispatch(rmkReceiptsOpened({ resourceId, registerId: String(mine.deviceId),
            cashier: mine.cashierName || mine.userDisplayName, day: isoDayOf(new Date()) }))
          openWindow(AQSI_WINDOWS.receipts, { rid: resourceId })
        },
        // RP-FB74: окна возврата открываются поверх РМК - продажа и режим РМК остаются на месте.
        ...(canRefund
          ? {
              openRefundSearch: () => openWindow(AQSI_WINDOWS.refundSearch, { rid: resourceId }),
              openSlipRefund: () => openWindow(AQSI_WINDOWS.slipRefund, { rid: resourceId }),
            }
          : {}),
      }),
    [canRefund, dispatch, mine, openWindow, resourceId, rmkVm],
  )
  return { window, resourceId, rmkVm, rmkActions, mine, registers, selectRegisters, selectMySession }
}
